import type {
  InviteUserAccessOnlySchema,
  UserScopeExclusionRecord,
  UserScopeRecord,
} from '../models';

const emptyAccess = (): InviteUserAccessOnlySchema => ({
  operators: {
    includeAll: false,
    selected: [],
    subOperators: {
      includeAll: false,
      selected: [],
      excluded: [],
    },
    excluded: [],
  },
  commerces: {
    includeAll: false,
    selected: [],
    excluded: [],
  },
  branches: {
    includeAll: false,
    selected: [],
    excluded: [],
  },
});

function mapCommerceOrBranch(
  type: 'commerce' | 'branch',
  scopes: UserScopeRecord[],
  exclusions: UserScopeExclusionRecord[]
): InviteUserAccessOnlySchema['commerces'] {
  const typeScopes = scopes.filter((s) => s.scopeType === type);
  const typeExcl = exclusions.filter((e) => e.scopeType === type);

  if (typeScopes.some((s) => s.scopeId === null)) {
    return {
      includeAll: true,
      selected: [],
      excluded: typeExcl.map((e) => e.scopeId),
    };
  }

  return {
    includeAll: false,
    selected: typeScopes
      .filter((s): s is UserScopeRecord & { scopeId: string } => s.scopeId !== null)
      .map((s) => s.scopeId),
    excluded: [],
  };
}

function isHeadquarterOperator(id: string, headquarterIds: Set<string>): boolean {
  return headquarterIds.has(id);
}

export function mapScopesToAccessForm(
  scopes: UserScopeRecord[],
  exclusions: UserScopeExclusionRecord[],
  headquarterIds: Set<string>
): InviteUserAccessOnlySchema {
  if (scopes.length === 0 && exclusions.length === 0) {
    return emptyAccess();
  }

  const opScopes = scopes.filter((s) => s.scopeType === 'operator');
  const opExclusions = exclusions.filter((e) => e.scopeType === 'operator');
  const hasOperatorAll = opScopes.some((s) => s.scopeId === null);

  let operatorsBlock: InviteUserAccessOnlySchema['operators'];

  if (hasOperatorAll) {
    const mainExcluded = opExclusions
      .filter((e) => isHeadquarterOperator(e.scopeId, headquarterIds))
      .map((e) => e.scopeId);

    const subScopeIds = opScopes
      .filter(
        (s): s is UserScopeRecord & { scopeId: string } =>
          s.scopeId !== null && !isHeadquarterOperator(s.scopeId, headquarterIds)
      )
      .map((s) => s.scopeId);

    const subExclusionsOnly = opExclusions.filter(
      (e) => !isHeadquarterOperator(e.scopeId, headquarterIds)
    );

    if (subScopeIds.length > 0) {
      operatorsBlock = {
        includeAll: true,
        selected: [],
        excluded: mainExcluded,
        subOperators: {
          includeAll: false,
          selected: subScopeIds,
          excluded: [],
        },
      };
    } else if (subExclusionsOnly.length > 0) {
      operatorsBlock = {
        includeAll: true,
        selected: [],
        excluded: mainExcluded,
        subOperators: {
          includeAll: true,
          selected: [],
          excluded: subExclusionsOnly.map((e) => e.scopeId),
        },
      };
    } else {
      operatorsBlock = {
        includeAll: true,
        selected: [],
        excluded: mainExcluded,
        subOperators: {
          includeAll: false,
          selected: [],
          excluded: [],
        },
      };
    }
  } else {
    const mainSelected = opScopes
      .filter(
        (s): s is UserScopeRecord & { scopeId: string } =>
          s.scopeId !== null && isHeadquarterOperator(s.scopeId, headquarterIds)
      )
      .map((s) => s.scopeId);

    const subScopeIds = opScopes
      .filter(
        (s): s is UserScopeRecord & { scopeId: string } =>
          s.scopeId !== null && !isHeadquarterOperator(s.scopeId, headquarterIds)
      )
      .map((s) => s.scopeId);

    const subExclusionsOnly = opExclusions.filter(
      (e) => !isHeadquarterOperator(e.scopeId, headquarterIds)
    );

    let subOperators: InviteUserAccessOnlySchema['operators']['subOperators'];

    if (subScopeIds.length > 0) {
      subOperators = {
        includeAll: false,
        selected: subScopeIds,
        excluded: [],
      };
    } else if (subExclusionsOnly.length > 0) {
      subOperators = {
        includeAll: true,
        selected: [],
        excluded: subExclusionsOnly.map((e) => e.scopeId),
      };
    } else {
      subOperators = {
        includeAll: false,
        selected: [],
        excluded: [],
      };
    }

    operatorsBlock = {
      includeAll: false,
      selected: mainSelected,
      excluded: [],
      subOperators,
    };
  }

  return {
    operators: operatorsBlock,
    commerces: mapCommerceOrBranch('commerce', scopes, exclusions),
    branches: mapCommerceOrBranch('branch', scopes, exclusions),
  };
}
